import { useState } from "react";
import { withTranslation } from "react-i18next";
import { useIntl } from "react-intl";
import { compose } from "redux";
import { useNavigate } from "react-router-dom";
import { OverlayTrigger, Spinner, Table, Tooltip } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import SVG from "react-inlinesvg";
import Select from "react-select";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderToolbar,
} from "../../../../_metronic/_partials/controls";
import SortDropdownHeader, {
  sortData,
} from "../../../components/common/SortDropdownHeader/SortDropdownHeader";
import {
  useDeleteInvestorSubscription,
  useGetSubscriptionPlans,
  useUpdateInvestorSubscription,
} from "../../../../swrHelpers/settings/revenueSettings";
import { formatCurrency } from "../../../../helpers/numberFormat";
import { toAbsoluteUrl } from "../../../../_metronic/_helpers";
import * as actions from "../../../../store/actions/index";

export const SponserSubscriptionsList = ({ generalSettings, demoMode }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState({
    column: null,
    direction: null,
  });

  const { manageOptionsLanguages, manageOptionsLanguageId } = useSelector(
    (state) => state.manageOptions
  );

  const { data: sponserPlans, mutate: sponserPlansMutate } =
    useGetSubscriptionPlans("SPONSER", manageOptionsLanguageId);

  const {
    data: deletedPlan,
    trigger: deletePlanTrigger,
    isMutating: deleteMutating,
  } = useDeleteInvestorSubscription();

  const handleSort = (column, direction) => {
    setSortConfig({ column, direction });
  };

  const addNew = () => {
    navigate("/revenue-settings/sponsor-fees/add-subscription");
  };

  const editPlan = (planId) => {
    navigate(`/revenue-settings/sponsor-fees/add-subscription/${planId}`);
  };

  const { trigger: updateSubscriptionTrigger, isMutating: updateMutating } =
    useUpdateInvestorSubscription();

  const handleActiveInactive = (item, status) => {
    const data = {
      status: status === true ? "YES" : "NO",
      id: item?.subscriptionLanguages?._id,
    };
    MySwal.fire({
      title: `${status === true ? "Active" : "Inactive"}  subscription plan `,
      text: `You want to ${status === true ? "Inactive" : "Active"} ${
        item?.subscriptionLanguages?.title
      } subscription plan ?`,
      icon: "warning",
      confirmButtonText: intl.formatMessage({ id: "GLOBAL.YES" }),
      showCancelButton: true,
      allowOutsideClick: false,
      allowEscapeKey: false,
      reverseButtons: true,
    }).then((result) => {
      if (result["isConfirmed"]) {
        updateSubscriptionTrigger(data);
        MySwal.fire({
          title: `${item?.subscriptionLanguages?.title} is now ${
            status === true ? "Inactive" : "Active"
          }`,
          text: "",
          icon: "success",
          showConfirmButton: true,
          timer: 3000,
        }).then((result) => {
          if (
            result.isConfirmed ||
            result.dismiss === MySwal.DismissReason.timer
          ) {
            sponserPlansMutate();
          }
        });
      }
    });
  };

  const MySwal = withReactContent(Swal);
  const deletePlan = (id) => {
    MySwal.fire({
      title: "Delete Subscription ?",
      text: "Are you sure you want to delete this subscription ?",
      icon: intl.formatMessage({ id: "WARNING" }),
      confirmButtonText: deleteMutating ? (
        <Spinner />
      ) : (
        intl.formatMessage({ id: "GLOBAL.YES" })
      ),
      allowOutsideClick: false,
      allowEscapeKey: false,
      showCancelButton: true,
      reverseButtons: true,
    }).then(async (result) => {
      if (result["isConfirmed"]) {
        await deletePlanTrigger({ id: id });
        sponserPlansMutate();
      }
    });
  };

  // Define custom sort functions for different columns
  const sortOptions = {
    name: (plan) => plan?.subscriptionLanguages?.title || "",
    amount: (plan) => plan?.amount ?? 0,
    campaignsAllowed: (plan) => plan?.campaignListingAllowedCount ?? 0,
    earlyBird: (plan) => (plan?.earlyBirdAccessstatus === "YES" ? 1 : 0),
    status: (plan) => (plan?.status === "YES" ? 1 : 0),
    isDefault: (plan) => (plan?.isDefault === "YES" ? 1 : 0),
  };

  // Get sorted plan data
  const sortedPlans = sponserPlans
    ? sortData(sponserPlans, sortConfig, sortOptions)
    : [];

  const investorPlanList =
    sortedPlans?.length > 0 ? (
      sortedPlans.map((plan, key) => (
        <tr key={key}>
          <td>{plan?.subscriptionLanguages?.title}</td>
          <td>
            {formatCurrency(
              plan?.amount ?? 0,
              generalSettings?.currencySymbolSide,
              generalSettings.currencyId?.symbol,
              generalSettings.currencyId?.code,
              generalSettings?.decimalPoints
            )}
          </td>
          <td>{plan?.campaignListingAllowedCount ?? 0}</td>
          <td>
            <span
              className={
                plan?.earlyBirdAccessstatus === "YES"
                  ? "label-light-success"
                  : "label-light-danger"
              }>
              {plan?.earlyBirdAccessstatus === "YES"
                ? "● Active"
                : "● Inactive"}
            </span>
          </td>
          <td>
            <span
              className={
                plan?.status === "YES"
                  ? "label-light-success"
                  : "label-light-danger"
              }>
              {plan?.status === "YES" ? "● Active" : "● Inactive"}
            </span>
          </td>
          <td>
            <span
              className={
                plan?.isDefault === "YES"
                  ? "label-light-success"
                  : "label-light-danger"
              }>
              {plan?.isDefault === "YES" ? "Yes" : "No"}{" "}
            </span>
          </td>
          <td className="text-start">
            <div className="w-120">
              <OverlayTrigger
                placement="bottom"
                popperConfig={{
                  modifiers: {
                    preventOverflow: {
                      enabled: false,
                    },
                  },
                }}
                overlay={<Tooltip>Edit</Tooltip>}>
                <button
                  onClick={() => editPlan(plan?._id)}
                  className="action-btn me-2">
                  <span className="btn-svg">
                    <SVG
                      title=" "
                      src={toAbsoluteUrl(
                        "media/svg/icons/Communication/Write.svg"
                      )}
                    />
                  </span>
                </button>
              </OverlayTrigger>
              {plan?.status === "NO" && (
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip>Active</Tooltip>}>
                  <button
                    onClick={() => handleActiveInactive(plan, true)}
                    className="action-btn-success me-2">
                    <span className="btn-svg">
                      <SVG
                        src={toAbsoluteUrl(
                          "media/svg/icons/Navigation/Check.svg"
                        )}
                      />
                    </span>
                  </button>
                </OverlayTrigger>
              )}
              {plan?.status === "YES" && (
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip>Inactive</Tooltip>}>
                  <button
                    onClick={() => handleActiveInactive(plan, false)}
                    className="action-btn-dark me-2">
                    <span className="btn-svg">
                      <SVG
                        src={toAbsoluteUrl(
                          "media/svg/icons/General/inactive.svg"
                        )}
                      />
                    </span>
                  </button>
                </OverlayTrigger>
              )}
              {demoMode === "false" &&
              plan?.isDefault === "NO" &&
              plan?.useCount === 0 ? (
                <OverlayTrigger
                  placement="bottom"
                  popperConfig={{
                    modifiers: {
                      preventOverflow: {
                        enabled: false,
                      },
                    },
                  }}
                  overlay={<Tooltip>Delete</Tooltip>}>
                  <button
                    onClick={() => deletePlan(plan?.subscriptionLanguages?._id)}
                    className="action-btn-danger">
                    <span className="btn-svg">
                      <SVG
                        title=""
                        src={toAbsoluteUrl("media/svg/icons/General/Trash.svg")}
                      />
                    </span>
                  </button>
                </OverlayTrigger>
              ) : null}
            </div>
          </td>
        </tr>
      ))
    ) : (
      <tr className="text-center">
        <td colSpan={7}>
          {sponserPlans?.length === 0 ? (
            <span>
              <img
                height={120}
                src={toAbsoluteUrl("media/animations/nodata-animation.gif")}
                alt="no data found!"
              />
              <br />
              <span className="text-primary fs-6 fw-bold">
                No Data Available!
              </span>
            </span>
          ) : (
            "Loading..."
          )}
        </td>
      </tr>
    );

  const languageOptions = manageOptionsLanguages
    .filter((option) => option?.status)
    .map((option) => ({
      value: option.id,
      label: option.name,
    }));

  const selectedLanguage = languageOptions.find(
    (option) => option.value === manageOptionsLanguageId
  );
  return (
    <Card className="mt-5">
      <CardHeader title={"Subscriptions"}>
        <div className="header-buttons d-flex">
          <CardHeaderToolbar className="me-3">
            <Select
              options={languageOptions}
              value={selectedLanguage}
              onChange={(selected) => {
                dispatch(actions.setManageOptionsCurrentId(selected.value));
              }}
              classNamePrefix="filter-bulk-select"
              isSearchable={false}
            />
          </CardHeaderToolbar>
          <CardHeaderToolbar>
            <button
              type="button"
              onClick={addNew}
              className="btn font-size-sm py-2 add-new-item-btn">
              Add New Subscription
            </button>
          </CardHeaderToolbar>
        </div>
      </CardHeader>
      <CardBody className="pt-0 px-4 pb-6">
        <div className="sticky-table-header-wrapper">
          <Table className="table-head-custom table-custom-bg table-vertical-center">
            <thead>
              <tr>
                <th>
                  <SortDropdownHeader
                    columnKey="name"
                    onSort={handleSort}
                    currentSort={sortConfig}>
                    Name
                  </SortDropdownHeader>
                </th>
                <th>
                  <SortDropdownHeader
                    columnKey="amount"
                    onSort={handleSort}
                    currentSort={sortConfig}>
                    Amount
                  </SortDropdownHeader>
                </th>
                <th>
                  <SortDropdownHeader
                    columnKey="campaignsAllowed"
                    onSort={handleSort}
                    currentSort={sortConfig}>
                    Campaigns Allowed
                  </SortDropdownHeader>
                </th>
                <th>
                  <SortDropdownHeader
                    columnKey="earlyBird"
                    onSort={handleSort}
                    currentSort={sortConfig}>
                    Early Bird
                  </SortDropdownHeader>
                </th>

                <th>
                  <SortDropdownHeader
                    columnKey="status"
                    onSort={handleSort}
                    currentSort={sortConfig}>
                    Status
                  </SortDropdownHeader>
                </th>
                <th>
                  <SortDropdownHeader
                    columnKey="isDefault"
                    align="right"
                    onSort={handleSort}
                    currentSort={sortConfig}>
                    Default
                  </SortDropdownHeader>
                </th>
                <th className="width110">Action</th>
              </tr>
            </thead>
            <tbody>{investorPlanList}</tbody>
          </Table>
        </div>
      </CardBody>
    </Card>
  );
};
export default compose(withTranslation())(SponserSubscriptionsList);
